import React from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

// MalPay Brand Color Palette - from mal-pay.com
const colors = {
  primary: {
    main: '#11C1EC', // Cyan/Teal - main brand color
    light: '#4DD4F2',
    dark: '#09486D', // Dark navy
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#09486D', // Dark navy
    light: '#1A6A9A',
    dark: '#063450',
    contrastText: '#ffffff',
  },
  success: {
    main: '#00C48C',
    light: '#33D0A3',
    dark: '#009A6E',
    contrastText: '#ffffff',
  },
  error: {
    main: '#FF4757',
    light: '#FF6B78',
    dark: '#E6303F',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#FFB946',
    light: '#FFC76B',
    dark: '#E6A03E',
    contrastText: '#ffffff',
  },
  info: {
    main: '#11C1EC',
    light: '#4DD4F2',
    dark: '#09486D',
    contrastText: '#ffffff',
  },
  grey: {
    50: '#F8F8FC',
    100: '#F0F0F6',
    200: '#E8E8F0',
    300: '#D0D0DC',
    400: '#B0B0C0',
    500: '#7F839C',
    600: '#565972',
    700: '#3D3F54',
    800: '#272A3A',
    900: '#202647',
  },
  background: {
    default: '#EEF2F9',
    paper: '#ffffff',
  },
  text: {
    primary: '#202647',
    secondary: '#7F839C',
  },
};

// Create the theme
export const theme = createTheme({
  palette: colors,
  typography: {
    fontFamily: '"Inter Variable", "Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontFamily: '"Plus Jakarta Sans Variable", "Plus Jakarta Sans", "Inter Variable", system-ui, sans-serif',
      fontSize: '2.5rem',
      fontWeight: 800,
      lineHeight: 1.2,
      color: '#202647',
    },
    h2: {
      fontFamily: '"Plus Jakarta Sans Variable", "Plus Jakarta Sans", "Inter Variable", system-ui, sans-serif',
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.3,
      color: '#202647',
    },
    h3: {
      fontFamily: '"Plus Jakarta Sans Variable", "Plus Jakarta Sans", "Inter Variable", system-ui, sans-serif',
      fontSize: '1.75rem',
      fontWeight: 700,
      lineHeight: 1.3,
      color: '#202647',
    },
    h4: {
      fontFamily: '"Plus Jakarta Sans Variable", "Plus Jakarta Sans", "Inter Variable", system-ui, sans-serif',
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: 1.4,
      color: '#202647',
    },
    h5: {
      fontFamily: '"Plus Jakarta Sans Variable", "Plus Jakarta Sans", "Inter Variable", system-ui, sans-serif',
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#272A3A',
    },
    h6: {
      fontFamily: '"Plus Jakarta Sans Variable", "Plus Jakarta Sans", "Inter Variable", system-ui, sans-serif',
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: '#272A3A',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#565972',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#7F839C',
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 25,
          padding: '10px 28px',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 15px rgba(17, 193, 236, 0.3)',
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #0EAED6 0%, #07395A 100%)',
            boxShadow: '0 6px 20px rgba(17, 193, 236, 0.4)',
          },
        },
        outlined: {
          borderColor: '#11C1EC',
          color: '#11C1EC',
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            borderColor: '#09486D',
            color: '#09486D',
            backgroundColor: 'rgba(17, 193, 236, 0.05)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 2px 20px rgba(9, 72, 109, 0.07), 0 1px 4px rgba(9, 72, 109, 0.04)',
          border: '1px solid rgba(17, 193, 236, 0.06)',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(12px)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 32px rgba(9, 72, 109, 0.12), 0 2px 8px rgba(17, 193, 236, 0.06)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#11C1EC',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#11C1EC',
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#202647',
          boxShadow: '0 1px 8px rgba(32, 38, 71, 0.06)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
          boxShadow: '2px 0 16px rgba(32, 38, 71, 0.06)',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 600,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 2px 16px rgba(9, 72, 109, 0.06), 0 1px 4px rgba(9, 72, 109, 0.03)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(17, 193, 236, 0.1)',
            color: '#09486D',
            '&:hover': {
              backgroundColor: 'rgba(17, 193, 236, 0.15)',
            },
            '& .MuiListItemIcon-root': {
              color: '#11C1EC',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(17, 193, 236, 0.05)',
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #0EAED6 0%, #07395A 100%)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

// Responsive breakpoints
export const breakpoints = {
  xs: '(max-width: 599px)',
  sm: '(min-width: 600px) and (max-width: 899px)',
  md: '(min-width: 900px) and (max-width: 1199px)',
  lg: '(min-width: 1200px) and (max-width: 1535px)',
  xl: '(min-width: 1536px)',
  mobile: '(max-width: 899px)',
  tablet: '(min-width: 600px) and (max-width: 1199px)',
  desktop: '(min-width: 1200px)',
};

// Theme provider component
export const MalPayThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

export default theme;
